/**
 * Générateur — Automatisme : reconnaître un parallélogramme à l'aide de sa
 * définition ou d'une propriété caractéristique grâce aux codages.
 * Attendu ID : 1122 | Niveau : 4ème | Domaine : Géométrie plane
 * Rubrique : AUTOMATISME (programme 2026, Parallélogrammes et translations 4e).
 * Labels (cartes exerciseur) :
 *   - Peut-on conclure ?             (QCM : une figure codée, « oui, car … » ou « non, le codage ne suffit pas » ;
 *     relier : trois figures ↔ ce que leur codage permet d'affirmer — carte FUSIONNÉE, décision prof 2026-09-10)
 *   - Retrouver le parallélogramme   (QCM : quatre figures codées, une seule prouve le parallélogramme)
 *   - Vrai ou faux ?                 (V/F sur une figure codée : lecture du codage, conclusion)
 *   - Le parallélogramme : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Parallélogrammes et translations 4e + grille P1-P16.
 * A. P1 : CM2 introduit (reconnaître un parallélogramme parmi les
 * quadrilatères), 6e consolidé (codages d'égalité, angle droit), 5e formalisé
 * (définition : côtés opposés parallèles ; propriétés caractéristiques des
 * côtés opposés et des diagonales), 4e automatisme. P2 : sens installés —
 * le parallélogramme comme forme ; sens 5e — le parallélogramme comme
 * quadrilatère DÉFINI par ses côtés parallèles, et CARACTÉRISÉ par ses côtés
 * opposés de même longueur, ses diagonales de même milieu, deux côtés opposés
 * parallèles et de même longueur. Distracteurs : une seule paire de côtés
 * parallèles (trapèze), une seule paire de côtés de même longueur, deux côtés
 * parallèles et les deux AUTRES de même longueur (trapèze isocèle), diagonales
 * de même longueur, une seule diagonale coupée en son milieu, deux côtés
 * consécutifs égaux (cerf-volant). P3 : aucun nombre. P4 : sans objet.
 * B. P5 : figure codée sur feuille blanche (traits d'égalité, milieu des
 * diagonales) ; le parallélisme est ÉCRIT sous la figure, « (AB) // (DC) »,
 * jamais fléché sur les côtés (décision prof 2026-09-10) ; moteur dessineQuad du 5e.
 * P6 : aucun instrument, lecture du codage. P7 : aucune mesure ; « seuls
 * les codages font foi » (doctrine prof, gen 1080). P8 : parallélogramme,
 * côtés opposés, diagonales, milieu, parallèles, codage.
 * C. P9 : lecture des codages d'égalité et d'angle droit (6e), codage de
 * parallélisme (5e), identification des parallélogrammes particuliers par
 * codage (1080). P10 : « d'après le codage, peut-on affirmer ? » — statut de
 * la propriété caractéristique (5e : admise). P11 : l'allure ne prouve rien ;
 * deux côtés parallèles ne suffisent pas ; le trapèze isocèle a deux côtés
 * égaux et deux côtés parallèles sans être un parallélogramme. P12 :
 * rémanence forte.
 * D. P13 : leurres = les six codages insuffisants ci-dessus, et pour le QCM de
 * justification, les autres propriétés (celle qui n'est pas codée). P14 :
 * aucun calcul. P15 : UNICITÉ — un seul codage prouvant par figure, donc une
 * seule justification vraie ; quand le codage est insuffisant, la figure
 * dessinée est six fois sur dix un VRAI parallélogramme (allure trompeuse,
 * revue 2026-09-10 : sinon l'allure donnait la réponse des planches) et
 * sinon un trapèze, un trapèze isocèle ou un cerf-volant. P16 : QCM à
 * justifications textuelles distinctes ; QCM sur planche à options A-D dans
 * l'ordre (figures numérotées 1 à 4) ; relier « Figure 1 » ↔ propriété.
 * INVARIANTS : le dessin respecte le codage (parallélisme et égalités
 * construits par vecteurs) ; data-motif / data-conclusion sur la figure pour
 * les solveurs ; V/F sans crochets, mixité 1-3 faux, familles exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { dessineQuad, milieu, dist } from '../../5eme/parallelogrammes/svgParallelo';

const ATTENDU_ID = 1122;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONCLURE = 'Peut-on conclure ?';
const L_RETROUVER = 'Retrouver le parallélogramme';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Le parallélogramme : vrai ou faux ?';

// ─── Vivier de motifs de codage ────────────────────────────────────────────
// codage : paralleles [[i,j,n]] (côté i→j ; les côtés de même n sont écrits parallèles sous la figure) ; egaux [[i,j,n]] (côté i→j, n traits) ;
// diagonales : { centre, demi: [['A','O',n], …] } (traits sur les demi-diagonales) ou { pleines: n } (traits sur [AC] et [BD]) ;
// consecutifs : côtés consécutifs égaux. conclusion : 'para' | 'long' | 'diag' | 'paralong' | null (insuffisant).
// forme : la figure dessinée, toujours compatible avec le codage.
const MOTIFS = [
    { id: 'para2', conclusion: 'para', forme: 'parallelogramme', paralleles: [[0, 1, 1], [3, 2, 1], [1, 2, 2], [0, 3, 2]] },
    { id: 'long2', conclusion: 'long', forme: 'parallelogramme', egaux: [[0, 1, 1], [3, 2, 1], [1, 2, 2], [0, 3, 2]] },
    { id: 'diag', conclusion: 'diag', forme: 'parallelogramme', diagonales: { centre: true, demi: [[0, 1], [2, 1], [1, 2], [3, 2]] } },
    { id: 'paralong', conclusion: 'paralong', forme: 'parallelogramme', paralleles: [[0, 1, 1], [3, 2, 1]], egaux: [[0, 1, 1], [3, 2, 1]] },
    { id: 'para1', conclusion: null, forme: 'trapeze', paralleles: [[0, 1, 1], [3, 2, 1]], raison: 'une seule paire de côtés parallèles' },
    { id: 'long1', conclusion: null, forme: 'trapeze_isocele', egaux: [[1, 2, 1], [0, 3, 1]], raison: 'une seule paire de côtés de même longueur' },
    { id: 'para1_long_autre', conclusion: null, forme: 'trapeze_isocele', paralleles: [[0, 1, 1], [3, 2, 1]], egaux: [[1, 2, 1], [0, 3, 1]], raison: 'deux côtés parallèles et les deux autres de même longueur' },
    { id: 'diag_egales', conclusion: null, forme: 'trapeze_isocele', diagonales: { pleines: 1 }, raison: 'des diagonales de même longueur' },
    { id: 'diag1', conclusion: null, forme: 'cerf_volant', diagonales: { centre: true, demi: [[1, 1], [3, 1]] }, raison: 'une seule diagonale coupée en son milieu' },
    { id: 'consec', conclusion: null, forme: 'cerf_volant', egaux: [[0, 1, 1], [0, 3, 1], [1, 2, 2], [3, 2, 2]], raison: 'des côtés consécutifs de même longueur' },
];
const PROUVANTS = MOTIFS.filter((m) => m.conclusion);
const INSUFFISANTS = MOTIFS.filter((m) => !m.conclusion);
const JUSTIF = {
    para: 'Oui : ses côtés opposés sont parallèles deux à deux.',
    long: 'Oui : ses côtés opposés sont de même longueur deux à deux.',
    diag: 'Oui : ses diagonales se coupent en leur milieu.',
    paralong: 'Oui : deux côtés opposés sont parallèles et de même longueur.',
    non: 'Non : le codage ne permet pas de conclure.',
};
const PROPRIETE = {
    para: 'les côtés opposés sont parallèles deux à deux',
    long: 'les côtés opposés sont de même longueur deux à deux',
    diag: 'les diagonales se coupent en leur milieu',
    paralong: 'deux côtés opposés sont parallèles et de même longueur',
};

// ─── Formes (repère écran, y vers le bas ; construites par vecteurs) ───────
const rot = (P, deg) => { const r = (deg * Math.PI) / 180; return { x: P.x * Math.cos(r) - P.y * Math.sin(r), y: P.x * Math.sin(r) + P.y * Math.cos(r) }; };
const sommets = (forme) => {
    const L = randInt(50, 70); const h = randInt(30, 42); const pente = randomChoice([-16, -10, 8, 12, 18]);
    let pts;
    if (forme === 'parallelogramme') pts = [{ x: 0, y: 0 }, { x: L, y: 0 }, { x: L + pente, y: -h }, { x: pente, y: -h }];
    else if (forme === 'rectangle') pts = [{ x: 0, y: 0 }, { x: L, y: 0 }, { x: L, y: -h }, { x: 0, y: -h }];
    else if (forme === 'losange') { const a = randInt(34, 44); const b = randInt(20, 26); pts = [{ x: 0, y: 0 }, { x: a, y: -b }, { x: 2 * a, y: 0 }, { x: a, y: b }]; }
    else if (forme === 'trapeze') { const c1 = randInt(6, 12); const c2 = randInt(16, 26); pts = [{ x: 0, y: 0 }, { x: L, y: 0 }, { x: L - c1, y: -h }, { x: c2, y: -h }]; }
    else if (forme === 'trapeze_isocele') { const c = randInt(10, 18); pts = [{ x: 0, y: 0 }, { x: L, y: 0 }, { x: L - c, y: -h }, { x: c, y: -h }]; }
    else { const d = randInt(56, 70); const p = randInt(16, 24); const w = randInt(22, 30); pts = [{ x: 0, y: 0 }, { x: w, y: -p }, { x: 0, y: -d }, { x: -w, y: -p }]; }   // cerf-volant : O milieu de [BD] seulement
    const deg = randomChoice([-12, -6, 0, 5, 10]);
    return pts.map((P) => rot(P, deg));
};
const NOMS = ['A', 'B', 'C', 'D'];
const VB = { w: 300, h: 200 };

/**
 * Mentions de parallélisme écrites SOUS la figure (décision prof, 2026-09-10) :
 * « (AB) // (DC) », les deux barres penchées, pas de flèches sur les côtés.
 * Une ligne par paire de côtés (les entrées de même n forment une paire).
 */
const mentionsParalleles = (motif, noms) => {
    const parN = {};
    (motif.paralleles || []).forEach(([i, j, n]) => { (parN[n] = parN[n] || []).push(`(${noms[i]}${noms[j]})`); });
    return Object.keys(parN).sort().map((n) => parN[n].join(' // '));
};
const BANDEAU = 22;   // hauteur d'une ligne de mention sous la figure
/** Traits d'égalité au milieu de [a b] (n traits). */
const traits = (fig, a, b, n, t0 = 0.5) => {
    const M = { x: a.x + (b.x - a.x) * t0, y: a.y + (b.y - a.y) * t0 }; const d = dist(a, b) || 1; const ux = (b.x - a.x) / d; const uy = (b.y - a.y) / d; const nx = -uy; const ny = ux;
    for (let k = 0; k < n; k++) {
        const t = (k - (n - 1) / 2) * 5; const cx = M.x + ux * t; const cy = M.y + uy * t;
        fig.brut(`<line class="tk" x1="${(cx + nx * 5).toFixed(1)}" y1="${(cy + ny * 5).toFixed(1)}" x2="${(cx - nx * 5).toFixed(1)}" y2="${(cy - ny * 5).toFixed(1)}" stroke="#0f172a" stroke-width="1.8"/>`);
    }
};

/**
 * Allure trompeuse (revue 2026-09-10, A2) : un codage insuffisant est dessiné,
 * six fois sur dix, sur un VRAI parallélogramme compatible avec le codage
 * (rectangle pour les diagonales égales, losange pour les côtés consécutifs).
 * Sans cela, la seule figure « qui ressemble à un parallélogramme » était
 * toujours la bonne réponse des planches. Le dessin ne contredit jamais le codage.
 */
const TROMPEUSE = { para1: 'parallelogramme', long1: 'parallelogramme', para1_long_autre: 'parallelogramme', diag_egales: 'rectangle', diag1: 'parallelogramme', consec: 'losange' };
const TAUX_TROMPEUSE = 0.6;

/** Figure codée d'un motif : { svg, motif, forme }. */
const figure = (motif, opt = {}) => {
    const forme = !motif.conclusion && (opt.trompeuse || Math.random() < TAUX_TROMPEUSE) ? TROMPEUSE[motif.id] : motif.forme;
    const pts = sommets(forme);
    const noms = opt.noms || NOMS;
    const diag = motif.diagonales;
    const { fig, P } = dessineQuad(pts, VB, `1122_${motif.id}_${randInt(1, 999)}`, {
        noms, diagonales: !!diag,
        codesCotes: (motif.egaux || []).map(([i, j, n]) => [i, j, n]),
    });
    const mentions = mentionsParalleles(motif, noms);
    if (diag) {
        // point d'intersection des diagonales : milieu de [AC] (parallélogramme), milieu de [BD] (cerf-volant)
        const inter = forme === 'cerf_volant' ? milieu(P[1], P[3]) : milieu(P[0], P[2]);
        if (diag.centre) {
            fig.brut(`<path class="cx" d="M ${(inter.x - 5).toFixed(1)},${inter.y.toFixed(1)} L ${(inter.x + 5).toFixed(1)},${inter.y.toFixed(1)} M ${inter.x.toFixed(1)},${(inter.y - 5).toFixed(1)} L ${inter.x.toFixed(1)},${(inter.y + 5).toFixed(1)}" stroke="#DC2626" stroke-width="1.6"/>`);
            if (noms.length) {
                // étiquette O sur la bissectrice du secteur (OC, OB) : à l'écart des deux diagonales
                const a1 = Math.atan2(P[2].y - inter.y, P[2].x - inter.x); const a2 = Math.atan2(P[1].y - inter.y, P[1].x - inter.x);
                let am = (a1 + a2) / 2; if (Math.abs(a1 - a2) > Math.PI) am += Math.PI;
                const lx = inter.x + 13 * Math.cos(am); const ly = inter.y + 13 * Math.sin(am) + 5;
                fig.brut(`<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-family="sans-serif" font-size="14" fill="#DC2626" text-anchor="middle">O</text>`);
            }
        }
        if (diag.demi) diag.demi.forEach(([i, n]) => traits(fig, P[i], inter, n));
        if (diag.pleines) { traits(fig, P[0], P[2], diag.pleines, 0.38); traits(fig, P[1], P[3], diag.pleines, 0.38); }
    }
    // bandeau des mentions de parallélisme sous la figure (le viewBox est agrandi d'autant)
    let svg = fig.render();
    if (mentions.length) {
        const H = VB.h + BANDEAU * mentions.length;
        svg = svg.replace(`viewBox="0 0 ${VB.w} ${VB.h}" width="${VB.w}" height="${VB.h}"`, `viewBox="0 0 ${VB.w} ${H}" width="${VB.w}" height="${H}"`);
        const textes = mentions.map((m, k) => `<text class="mp" x="${VB.w / 2}" y="${VB.h + BANDEAU * k + 12}" font-family="sans-serif" font-size="17" font-weight="600" fill="#0f172a" text-anchor="middle">${m}</text>`).join('');
        svg = svg.replace('</svg>', `${textes}</svg>`);
    }
    svg = svg.replace('<svg ', `<svg data-motif="${motif.id}" data-conclusion="${motif.conclusion || 'non'}" data-forme="${forme}" data-paralleles="${mentions.join('|')}" `);
    return { svg, motif, forme };
};

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
/** « Peut-on conclure ? » : le code d'une réponse fausse r quand le codage prouve `conclusion` (null : codage insuffisant). */
const codeConclusion = (conclusion, r) => {
    const oui = r.startsWith('Oui');
    if (conclusion) return oui ? 'lit_la_figure_pas_le_codage' : 'refus_du_codage';
    return oui ? 'hypothese_manquante' : null;
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refC = { v: null }; const refR = { v: null }; const refP = { v: null };

// ─── Carte 1 : peut-on conclure ? (QCM à justifications) ───────────────────

const construireConclure = () => {
    // un motif prouvant une fois sur deux, insuffisant sinon
    const motif = Math.random() < 0.5 ? randomChoice(PROUVANTS) : randomChoice(INSUFFISANTS);
    const F = figure(motif);
    const bonne = JUSTIF[motif.conclusion || 'non'];
    // « Non » est TOUJOURS proposé (revue 2026-09-10, A2) : sa présence ne renseigne pas sur la réponse
    const ouiAutres = shuffle(['para', 'long', 'diag', 'paralong'].filter((k) => k !== motif.conclusion)).slice(0, motif.conclusion ? 2 : 3).map((k) => JUSTIF[k]);
    const autres = motif.conclusion ? [JUSTIF.non, ...ouiAutres] : ouiAutres;
    const options = shuffle([bonne, ...autres]);
    const payload = makeQcm(options, bonne, '<p>ABCD est un quadrilatère. D\'après le codage, peut-on affirmer que ABCD est un parallélogramme ?</p>', { ...opts(L_CONCLURE), elements_additionnels: imgElement(F.svg, 'medium') });
    payload.contenu = { ...payload.contenu, sousType: motif.conclusion || 'non' };
    payload._meta.params = { motif: motif.id, conclusion: motif.conclusion, forme: F.forme, signature: `${motif.id}-${[...options].sort().join('|')}` };
    // refuser de conclure, conclure sur un codage insuffisant, ou justifier par une propriété non codée
    const erreurs = options.filter((o) => o !== bonne).map((o) => ({ code: codeConclusion(motif.conclusion, o), valeur: o })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genConclure = () => antiRepeat(construireConclure, refC);

// ─── Carte 2 : retrouver le parallélogramme (planche de quatre figures) ─────

const planche = (figs) => {
    // trois figures : une ligne ; quatre : deux lignes de deux
    const cols = figs.length <= 3 ? figs.length : 2; const rows = Math.ceil(figs.length / cols);
    const w = 210; const h = 165; const W = cols * w; const H = rows * h;
    let g = `<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>`;
    figs.forEach((f, i) => {
        const vbm = f.svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/); const vw = Number(vbm[1]); const vh = Number(vbm[2]);
        const contenu = f.svg.replace(/^<svg [^>]*>/, '').replace(/<\/svg>\s*$/, '');
        const c = i % cols; const r = Math.floor(i / cols);
        const sc = Math.min((w - 8) / vw, (h - 26) / vh); const tx = c * w + (w - sc * vw) / 2; const ty = r * h + 22 + (h - 26 - sc * vh) / 2;
        g += `<g transform="translate(${tx.toFixed(1)},${ty.toFixed(1)}) scale(${sc.toFixed(4)})">${contenu}</g>`;
        g += `<text x="${c * w + w / 2}" y="${r * h + 16}" font-family="system-ui,sans-serif" font-size="15" font-weight="800" fill="#1e3a8a" text-anchor="middle">Figure ${f.label}</text>`;
    });
    const data = figs.map((f) => `${f.label}:${f.motif.id}:${f.motif.conclusion || 'non'}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" data-figures="${data}">${g}</svg>`;
};

const construireRetrouver = () => {
    const prouvant = randomChoice(PROUVANTS); const leurres = shuffle(INSUFFISANTS).slice(0, 3);
    // figures numérotées 1 à 4 : chacune garde ses sommets A, B, C, D (les mentions de parallélisme les nomment)
    const lettres = ['1', '2', '3', '4'];
    const motifs = shuffle([prouvant, ...leurres]);
    const figs = motifs.map((m, i) => ({ ...figure(m), label: lettres[i] }));
    const bonne = figs.find((f) => f.motif.conclusion).label;
    const payload = makeQcm(lettres, bonne, '<p>Voici quatre quadrilatères codés. D\'après son codage, lequel est certainement un parallélogramme ?</p>', { ...opts(L_RETROUVER), elements_additionnels: imgElement(planche(figs), 'large') });
    // options dans l'ordre 1-4 ; la clé de correction est la LETTRE de position (A-D), pas le numéro (revue 2026-09-10, A1)
    payload.contenu.choix = lettres; payload.contenu.bonne_reponse = 'ABCD'[lettres.indexOf(bonne)];
    payload.contenu = { ...payload.contenu, sousType: prouvant.conclusion };
    payload._meta.params = { prouvant: prouvant.id, leurres: leurres.map((m) => m.id), signature: `${prouvant.id}-${motifs.map((m) => m.id).join(',')}` };
    // toute autre figure porte un codage insuffisant : conclure avec trop peu
    const erreurs = figs.filter((f) => f.label !== bonne).map((f) => ({ code: 'hypothese_manquante', valeur: f.label }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genRetrouver = () => antiRepeat(construireRetrouver, refR);

// ─── Carte 1 bis : peut-on conclure ? (relier, même carte que le QCM) ─────────

const construirePropriete = () => {
    // trois figures de conclusions distinctes (dont une insuffisante une fois sur deux), une cible leurre
    const cles = shuffle(['para', 'long', 'diag', 'paralong']);
    const choisis = Math.random() < 0.5 ? [...cles.slice(0, 2).map((k) => PROUVANTS.find((m) => m.conclusion === k)), randomChoice(INSUFFISANTS)] : cles.slice(0, 3).map((k) => PROUVANTS.find((m) => m.conclusion === k));
    const lettres = ['1', '2', '3'];
    // relier : les trois figures ont l'allure d'un parallélogramme, seul le codage départage (revue 2026-09-10, A2)
    const figs = shuffle(choisis).map((m, i) => ({ ...figure(m, { trompeuse: true }), label: lettres[i] }));
    const cible = (m) => (m.conclusion ? `Parallélogramme : ${PROPRIETE[m.conclusion]}.` : 'Le codage ne permet pas de conclure.');
    const pairs = figs.map((f) => ({ source: `Figure ${f.label}`, target: cible(f.motif) }));
    const cibles = pairs.map((p) => p.target);
    const restantes = ['para', 'long', 'diag', 'paralong'].map((k) => `Parallélogramme : ${PROPRIETE[k]}.`).filter((t) => !cibles.includes(t));
    if (!cibles.includes('Le codage ne permet pas de conclure.')) restantes.push('Le codage ne permet pas de conclure.');
    // code par cible quand les trois figures prouvent (refuser de conclure ; une propriété non codée) ; sinon la cible dépend de la figure reliée : sans code
    const tousProuvent = choisis.every((m) => m.conclusion);
    const leurres = shuffle(restantes).slice(0, 2).map((t) => (tousProuvent ? { target: t, code: t === 'Le codage ne permet pas de conclure.' ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage' } : { target: t }));
    const payload = makeRelier(pairs, '<p>Relie chaque figure à ce que son codage permet d\'affirmer.</p>', { ...opts(L_CONCLURE), elements_additionnels: imgElement(planche(figs), 'large'), cibles_leurres: leurres });
    payload._meta.params = { motifs: figs.map((f) => f.motif.id), signature: figs.map((f) => f.motif.id).sort().join(',') };
    return payload;
};
const genPropriete = () => antiRepeat(construirePropriete, refP);

// ─── Carte 4 : vrai ou faux ? (sur une figure codée) ───────────────────────

let derniereEmpreinteVf1122 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const motif = Math.random() < 0.5 ? randomChoice(PROUVANTS) : randomChoice(INSUFFISANTS);
        const F = figure(motif);
        // deux côtés sont « codés » parallèles / égaux si les deux portent la MÊME marque (même n de mention ou même nombre de traits)
        const memeCode = (liste, i, j, k, l) => {
            const code = (a, b) => (liste || []).find(([p, q]) => (p === a && q === b) || (p === b && q === a));
            const c1 = code(i, j); const c2 = code(k, l);
            return !!(c1 && c2 && c1[2] === c2[2]);
        };
        const aPara = (i, j) => memeCode(motif.paralleles, i, j, 3, 2);
        const aEgal = (i, j) => memeCode(motif.egaux, i, j, 0, 3);
        const codeMilieu = !!(motif.diagonales && motif.diagonales.centre && motif.diagonales.demi && motif.diagonales.demi.length === 4);
        const seg = (i, j) => `[${NOMS[i]}${NOMS[j]}]`;
        const familles = [
            { f: 'conclusion', vrai: motif.conclusion ? 'D\'après le codage, ABCD est un parallélogramme.' : 'Le codage ne permet pas d\'affirmer que ABCD est un parallélogramme.', faux: motif.conclusion ? 'Le codage ne permet pas d\'affirmer que ABCD est un parallélogramme.' : 'D\'après le codage, ABCD est un parallélogramme.' },
            { f: 'parallele', vrai: aPara(0, 1) ? 'Il est indiqué que (AB) et (DC) sont parallèles.' : 'Rien n\'indique que (AB) et (DC) sont parallèles.', faux: aPara(0, 1) ? 'Rien n\'indique que (AB) et (DC) sont parallèles.' : 'Il est indiqué que (AB) et (DC) sont parallèles.' },
            { f: 'longueur', vrai: aEgal(1, 2) ? `Les côtés ${seg(1, 2)} et ${seg(0, 3)} sont codés de même longueur.` : `Les côtés ${seg(1, 2)} et ${seg(0, 3)} ne sont pas codés de même longueur.`, faux: aEgal(1, 2) ? `Les côtés ${seg(1, 2)} et ${seg(0, 3)} ne sont pas codés de même longueur.` : `Les côtés ${seg(1, 2)} et ${seg(0, 3)} sont codés de même longueur.` },
            { f: 'diagonales', vrai: codeMilieu ? 'Les diagonales sont codées comme se coupant en leur milieu.' : 'Le codage ne dit pas que les diagonales se coupent en leur milieu.', faux: codeMilieu ? 'Le codage ne dit pas que les diagonales se coupent en leur milieu.' : 'Les diagonales sont codées comme se coupant en leur milieu.' },
            { f: 'suffit', vrai: motif.conclusion ? `Ce codage suffit : ${PROPRIETE[motif.conclusion]}.` : `Ce codage ne suffit pas : ${motif.raison}.`, faux: motif.conclusion ? 'Il faudrait aussi mesurer pour être sûr.' : `Ce codage suffit : ${PROPRIETE[randomChoice(['para', 'long', 'diag', 'paralong'])]}.` },
        ];
        // « conclusion » et « suffit » portent sur la même idée : une seule des deux par tirage (A8)
        const exclue = randomChoice(['conclusion', 'suffit']);
        const retenues = shuffle(familles.filter((fa) => fa.f !== exclue));
        const nbFaux = randInt(1, 3);
        const ordre = shuffle(retenues); const lignes = ordre.map((fa, i) => (i < nbFaux ? { l: fa.faux, v: false, f: fa.f } : { l: fa.vrai, v: true, f: fa.f }));
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1122) continue;
        derniereEmpreinteVf1122 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            '<p>ABCD est un quadrilatère codé. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: imgElement(F.svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { motif: motif.id, conclusion: motif.conclusion, nbFaux };
        // lui faire dire ce qu'il ne dit pas, ou conclure avec trop peu ; refus_du_codage seulement pour « Il faudrait
        // aussi mesurer » : nier une marque codée ou la conclusion se lit aussi comme une négation mal lue ou un codage
        // pas su, la réponse ne dit pas laquelle (juge du 03/10, décision du prof 04/10/2026) : ces lignes partent sans nom
        const codeFaux = {
            conclusion: motif.conclusion ? null : 'hypothese_manquante',
            parallele: aPara(0, 1) ? null : 'lit_la_figure_pas_le_codage',
            longueur: aEgal(1, 2) ? null : 'lit_la_figure_pas_le_codage',
            diagonales: codeMilieu ? null : 'lit_la_figure_pas_le_codage',
            suffit: motif.conclusion ? 'refus_du_codage' : 'hypothese_manquante',
        };
        const erreurs = melange.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1122 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : le parallélogramme, vrai ou faux ? (pur rappel) ─────────────

const POOL_RAPPEL = [
    { l: 'Un quadrilatère dont les côtés opposés sont parallèles deux à deux est un parallélogramme.', v: true, f: 'definition' },
    { l: 'Un quadrilatère qui a seulement deux côtés parallèles est un parallélogramme.', v: false, f: 'definition', code: 'hypothese_manquante' },
    { l: 'Un quadrilatère dont les côtés opposés sont de même longueur deux à deux est un parallélogramme.', v: true, f: 'longueurs' },
    { l: 'Un quadrilatère qui a deux côtés opposés de même longueur est toujours un parallélogramme.', v: false, f: 'longueurs', code: 'hypothese_manquante' },
    { l: 'Un quadrilatère dont les diagonales se coupent en leur milieu est un parallélogramme.', v: true, f: 'diagonales' },
    { l: 'Un quadrilatère dont les diagonales ont la même longueur est un parallélogramme.', v: false, f: 'diagonales', code: 'hypothese_manquante' },
    { l: 'Un quadrilatère qui a deux côtés opposés parallèles et de même longueur est un parallélogramme.', v: true, f: 'paralong' },
    { l: 'Un quadrilatère qui a deux côtés parallèles et deux autres côtés de même longueur est un parallélogramme.', v: false, f: 'paralong', code: 'hypothese_manquante' },
    { l: 'Seuls les codages de la figure permettent d\'affirmer qu\'un quadrilatère est un parallélogramme.', v: true, f: 'codage' },
    { l: 'Si un quadrilatère a l\'air d\'un parallélogramme, on peut affirmer que c\'en est un.', v: false, f: 'codage', code: 'lit_la_figure_pas_le_codage' },
    { l: 'Un rectangle, un losange et un carré sont des parallélogrammes.', v: true, f: 'famille' },
    { l: 'Un trapèze est toujours un parallélogramme.', v: false, f: 'famille', code: 'inclusion_inversee' },
    { l: 'Dans un parallélogramme, les diagonales se coupent en leur milieu.', v: true, f: 'propriete' },
    { l: 'Dans un parallélogramme, les diagonales ont toujours la même longueur.', v: false, f: 'propriete', code: 'cas_particulier_generalise' },
    // angles et centre de symétrie (ajout prof 2026-09-10)
    { l: 'Dans un parallélogramme, les angles opposés ont la même mesure.', v: true, f: 'angles' },
    { l: 'Dans un parallélogramme, deux angles consécutifs ont toujours la même mesure.', v: false, f: 'angles', code: 'opposes_consecutifs_confondus' },
    { l: 'Dans un parallélogramme, deux angles consécutifs sont supplémentaires.', v: true, f: 'angles' },
    { l: 'Le point d\'intersection des diagonales d\'un parallélogramme est son centre de symétrie.', v: true, f: 'symetrie' },
    { l: 'Un parallélogramme a toujours un axe de symétrie.', v: false, f: 'symetrie' },
    { l: 'Un quadrilatère qui a un centre de symétrie est un parallélogramme.', v: true, f: 'symetrie' },
];

let dernieresLignesRappel1122 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1122 = [];
        if (lignes.some((it) => dernieresLignesRappel1122.includes(it.l))) continue;
        dernieresLignesRappel1122 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur le parallélogramme, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier (aucun pour « toujours un axe de symétrie »)
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1122 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    conclure_qcm_unique: genConclure,
    retrouver_qcm_unique: genRetrouver,
    conclure_relier: genPropriete,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'conclure', label: L_CONCLURE, formatsList: buildFormats('conclure') },
    { id: 'retrouver', label: L_RETROUVER, formatsList: buildFormats('retrouver') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    conclure_qcm_unique: ['refus_du_codage', 'hypothese_manquante'],
    retrouver_qcm_unique: ['hypothese_manquante'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { MOTIFS, JUSTIF, PROPRIETE, figure, sommets, POOL_RAPPEL };
